import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

const TariffeBulkPreviewTable = ({ tariffe, onUpdate, onSaveAll }) => {
  const [rows, setRows] = useState(
    tariffe.map((tariff, index) => ({
      id: index,
      ...tariff,
    }))
  );

  const handleProcessRowUpdate = (newRow) => {
    const updatedRows = rows.map((row) => (row.id === newRow.id ? newRow : row));
    setRows(updatedRows);
    
    // Notify parent of changes
    const updatedTariffe = updatedRows.map(({ id, ...rest }) => rest);
    onUpdate(updatedTariffe);
    
    return newRow;
  };

  const columns = [
    {
      field: "fornitore",
      headerName: "FORNITORE",
      width: 150,
      editable: true,
    },
    {
      field: "nomeOfferta",
      headerName: "OFFERTA",
      width: 200,
      editable: true,
    },
    {
      field: "tipo",
      headerName: "TIPO",
      width: 100,
      editable: true,
      type: "singleSelect",
      valueOptions: ["Luce", "Gas"],
    },
    {
      field: "prezzo",
      headerName: "PREZZO (€)",
      width: 120,
      editable: true,
      type: "number",
      valueGetter: (params) => params.row.prezzoMateriaPrima?.valore,
      valueSetter: (params) => {
        return { 
          ...params.row, 
          prezzoMateriaPrima: {
            ...params.row.prezzoMateriaPrima,
            valore: params.value
          }
        };
      },
      valueFormatter: (params) => {
        if (params.value == null) return "";
        return `€${params.value.toFixed(4)}`;
      },
    },
    {
      field: "spread",
      headerName: "SPREAD (€)",
      width: 120,
      editable: true,
      type: "number",
      valueFormatter: (params) => {
        if (params.value == null) return "";
        return `€${params.value.toFixed(4)}`;
      },
    },
    {
      field: "pcv",
      headerName: "PCV (€/M)",
      width: 120,
      editable: true,
      type: "number",
      valueGetter: (params) => params.row.pcv?.valoreMensile,
      valueSetter: (params) => {
        return { 
          ...params.row, 
          pcv: {
            ...params.row.pcv,
            valoreMensile: params.value
          }
        };
      },
      valueFormatter: (params) => {
        if (params.value == null) return "";
        return `€${params.value.toFixed(4)}`;
      },
    },
    {
      field: "note",
      headerName: "NOTE",
      width: 200,
      editable: true,
    },
    {
      field: "confidenceScore",
      headerName: "AI CONF.",
      width: 110,
      flex: 0,
      renderCell: (params) => {
        const score = params.value || 0;
        const color = score >= 90 ? "success" : score >= 80 ? "warning" : "error";
        const label = score < 85 ? "REVIEW" : "";
        
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, py: 0.5 }}>
            <Chip
              label={`${score}%`}
              color={color}
              size="small"
              sx={{ height: 20, fontSize: "0.75rem" }}
            />
            {label && (
              <Typography
                variant="caption"
                color="error"
                sx={{ fontSize: "0.65rem", fontWeight: 600 }}
              >
                {label}
              </Typography>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        width: "100%", 
        p: 2, 
        mb: 3,
        backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'background.paper' : '#fff',
      }}
    >
      <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6">
          Modifica Anteprima Estrazione ({rows.length})
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="outlined" onClick={() => setRows(tariffe.map((t, i) => ({ id: i, ...t })))}>
            Annulla Modifiche
          </Button>
          <Button variant="contained" onClick={() => onSaveAll(rows.map(({ id, ...rest }) => rest))}>
            Salva Definitivamente
          </Button>
        </Box>
      </Box>
      
      <Box sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          processRowUpdate={handleProcessRowUpdate}
          disableSelectionOnClick
          disableColumnMenu
          disableRowSelectionOnClick
          hideFooterSelectedRowCount
          checkboxSelection={false}
          columnHeaderHeight={56}
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            "& .MuiDataGrid-cell": {
              borderRight: (theme) => `1px solid ${theme.palette.divider}`,
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: (theme) => 
                theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.05)' 
                  : 'rgba(0, 0, 0, 0.04)',
              fontWeight: 600,
              borderBottom: (theme) => `2px solid ${theme.palette.divider}`,
            },
            "& .MuiDataGrid-row": {
              "&:hover": {
                backgroundColor: (theme) => 
                  theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.08)' 
                    : 'rgba(0, 0, 0, 0.04)',
              },
            },
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-cell:focus-within": {
              outline: (theme) => `2px solid ${theme.palette.primary.main}`,
              outlineOffset: "-1px",
            },
            "& .MuiDataGrid-filler": {
              display: "none",
            },
            "& .MuiDataGrid-scrollbar--horizontal": {
              display: "none",
            },
          }}
        />
      </Box>
    </Paper>
  );
};

export default TariffeBulkPreviewTable;
